import React from "react";
import { Link } from "react-router-dom";

const rooms = [
  {
    name: "Summit View Room",
    description:
      "Wake up to K-2 and Broad Peak filling your window. Our signature room with a private balcony overlooking the Karakoram ridge line.",
    price: "$45",
    per: "per night",
    badge: "Most Popular",
    badgeClass: "badge-brand",
    capacity: "2 guests",
    size: "28 m²",
    image:
      "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80",
    features: ["Private balcony", "Mountain view", "King bed", "Ensuite bath"],
    accent: "ocean",
  },
  {
    name: "Indus River Suite",
    description:
      "Spacious family suite with panoramic views of the silver Indus winding through the valley below. Perfect for families.",
    price: "$65",
    per: "per night",
    badge: "Family Pick",
    badgeClass: "badge-teal",
    capacity: "4 guests",
    size: "45 m²",
    image:
      "https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800&q=80",
    features: ["Living area", "River view", "Two beds", "Full kitchen"],
    accent: "teal",
  },
  {
    name: "Mountain View Room",
    description:
      "A snug mountain retreat with hand-crafted wooden interiors, warm quilts, and everything you need for a restful night after trekking.",
    price: "$30",
    per: "per night",
    badge: "Best Value",
    badgeClass: "badge-sand",
    capacity: "2 guests",
    size: "18 m²",
    image:
      "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=800&q=80",
    features: ["Garden view", "Twin beds", "Shared bath", "Heater"],
    accent: "sand",
  },
];

const AccentConfig = {
  ocean: {
    iconBg: "bg-ocean-100 text-ocean-700",
    badgeBg: "bg-ocean-50 border-ocean-200",
    price: "text-ocean-700",
    btn: "bg-ocean-700 hover:bg-ocean-800 text-white shadow-ocean",
    border: "hover:border-ocean-300",
    featureDot: "bg-ocean-400",
  },
  teal: {
    iconBg: "bg-teal-100 text-teal-700",
    badgeBg: "bg-teal-50 border-teal-200",
    price: "text-teal-700",
    btn: "bg-teal-600 hover:bg-teal-700 text-white shadow-teal",
    border: "hover:border-teal-300",
    featureDot: "bg-teal-400",
  },
  sand: {
    iconBg: "bg-sand-100 text-sand-700",
    badgeBg: "bg-sand-50 border-sand-200",
    price: "text-sand-700",
    btn: "bg-sand-500 hover:bg-sand-600 text-white shadow-sand",
    border: "hover:border-sand-300",
    featureDot: "bg-sand-400",
  },
};

export default function Rooms() {
  return (
    <section id="rooms" className="section-pad bg-section-light">
      <div className="container-base">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-subtitle mb-3">Accommodations</p>
          <h2 className="section-title mb-4">Rooms &amp; Suites</h2>
          <p className="section-lead">
            Each room is thoughtfully designed to connect you with the raw
            beauty of the Karakoram — comfort that never compromises on
            adventure.
          </p>
        </div>

        {/* Room cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms.map((room, i) => {
            const ac = AccentConfig[room.accent];
            return (
              <article
                key={i}
                className={`card group flex flex-col border border-slate-100 ${ac.border} hover:-translate-y-1 transition-all duration-300`}
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={room.image}
                    alt={room.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                  {/* Badge */}
                  <span
                    className={`absolute top-3 right-3 px-3 py-1 rounded-full text-body-xs font-semibold border ${ac.badgeBg}`}
                  >
                    {room.badge}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  {/* Meta */}
                  <div className="flex items-center gap-4 text-body-xs text-slate-400 mb-3">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {room.capacity}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      {room.size}
                    </span>
                  </div>

                  <h3 className="font-serif font-bold text-xl text-slate-900 mb-2">
                    {room.name}
                  </h3>
                  <p className="text-body-sm text-slate-500 leading-relaxed mb-4 flex-1">
                    {room.description}
                  </p>

                  {/* Features */}
                  <ul className="grid grid-cols-2 gap-1.5 mb-5">
                    {room.features.map((feat) => (
                      <li key={feat} className="flex items-center gap-1.5 text-body-xs text-slate-600">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ac.featureDot}`} />
                        {feat}
                      </li>
                    ))}
                  </ul>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <span className={`font-serif font-bold text-2xl ${ac.price}`}>
                        {room.price}
                      </span>
                      <span className="text-body-xs text-slate-400 ml-1">{room.per}</span>
                    </div>
                    <Link
                      to="/booking"
                      className={`px-5 py-2 rounded-full text-body-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${ac.btn}`}
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-500 text-body-sm mb-4">
            All rates include breakfast, Wi-Fi &amp; complimentary airport transfer.
          </p>
          <Link to="/booking" className="btn-primary">
            Check Availability
          </Link>
        </div>
      </div>
    </section>
  );
}
